/**
 * <sky-quick-view> — Sky Quick View drawer.
 *
 * Any `[data-sky-quick-view]` link opens it: the product is fetched through the Section Rendering API
 * (`<product url>?section_id=sky-quick-view`) and shown in the drawer. Closes on backdrop / close button /
 * Escape, and after a successful add to cart (the cart drawer takes over).
 */
import { StandardEvents } from '@shopify/events';

class SkyQuickView extends HTMLElement {
  connectedCallback() {
    this.loadStyles();
    this.panel = this.querySelector('.sky-cd__panel');
    this.body = /** @type {HTMLElement} */ (this.querySelector('[data-qv-body]'));
    this.cache = new Map();

    this.onDocumentClick = (event) => {
      const link = event.target instanceof Element && event.target.closest('a[data-sky-quick-view]');
      if (!(link instanceof HTMLAnchorElement) || event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      this.opener = link;
      this.show(link.href);
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    };
    // Close after a successful add from inside the quick view
    this.onLinesUpdate = (event) => {
      if (!this.classList.contains('is-open') || !this.contains(/** @type {Node} */ (event.target))) return;
      event.promise?.then(({ detail }) => !detail?.didError && this.close()).catch(() => {});
    };

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onLinesUpdate);
    this.addEventListener('click', (event) => {
      if (/** @type {Element} */ (event.target).closest('[data-qv-close]')) this.close();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onLinesUpdate);
  }

  /**
   * Add the drawer's stylesheets at the top of <head> unless the page already has them, so section styles
   * that load later (e.g. sky-main-product.css, sky-main-cart.css) keep winning the cascade.
   */
  loadStyles() {
    const first = document.head.querySelector('link[rel="stylesheet"], style');
    for (const href of (this.dataset.styles || '').split(' ').filter(Boolean)) {
      const file = new URL(href, window.location.href).pathname;
      const loaded = [...document.querySelectorAll('link[rel="stylesheet"]')].some(
        (link) => new URL(/** @type {HTMLLinkElement} */ (link).href).pathname === file
      );
      if (loaded) continue;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.insertBefore(link, first);
    }
  }

  /** @param {string} productUrl */
  async show(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', 'sky-quick-view');
    const key = url.toString();

    this.body.replaceChildren();
    this.classList.add('is-loading');
    this.open();

    try {
      if (!this.cache.has(key)) this.cache.set(key, await (await fetch(key)).text());
      const doc = new DOMParser().parseFromString(this.cache.get(key), 'text/html');
      const content = doc.querySelector('.shopify-section') || doc.querySelector('[data-qv-content]');
      if (!content) throw new Error('No quick view content');
      this.body.replaceChildren(document.importNode(content, true));
      this.body.scrollTop = 0;
    } catch {
      this.cache.delete(key);
      window.location.href = productUrl;
    } finally {
      this.classList.remove('is-loading');
    }
  }

  open() {
    if (this.classList.contains('is-open')) return;
    clearTimeout(this.timer);
    this.hidden = false;
    document.documentElement.setAttribute('scroll-lock', '');
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        /** @type {HTMLElement | null} */ (this.panel)?.focus({ preventScroll: true });
      })
    );
  }

  close() {
    this.classList.remove('is-open');
    this.timer = setTimeout(() => {
      this.hidden = true;
      this.body.replaceChildren();
      // the cart drawer may have opened meanwhile and still needs the lock
      if (!document.querySelector('sky-cart-drawer.is-open')) document.documentElement.removeAttribute('scroll-lock');
    }, 600);
    this.opener?.focus({ preventScroll: true });
  }
}

if (!customElements.get('sky-quick-view')) customElements.define('sky-quick-view', SkyQuickView);
